import { prefersReducedMotion } from '@theme/utilities';

// Approximates Webflow's smoothing:50 lerp from the original IX2 "Trees reveal" interaction.
const SMOOTHING = 0.12;

// Below this delta the eased value is visually settled.
const EPSILON = 0.0001;

const DEFAULT_ANIMATION_LENGTH_VH = 100;

// The prototype's margin-bottom shrank its sticky constraint rect, pushing the trees up by that much
// at the page bottom: 12vw on desktop, 25vw at its 991px breakpoint. We pin with fixed, which has no
// such constraint, so the lift is reproduced explicitly here.
const END_LIFT_VW = 12;
const END_LIFT_VW_MOBILE = 25;

// Theme convention is a single mobile breakpoint at 749px (the prototype stepped at 991px).
const MOBILE_QUERY = matchMedia('(max-width: 749px)');

// A viewport of slack each side, so the lerp can settle before the loop is gated off.
const ROOT_MARGIN = '100% 0px';

/**
 * Scroll-scrubbed parallax for the WOM Trees section.
 *
 * Pinning is done in JS with position: fixed rather than CSS sticky. Sticky is always bounded by its
 * containing block, which would release the trees at the end of main; fixed is viewport-relative and
 * has no such bound, so they carry on past the footer.
 *
 * Writes `--wom-trees-progress` (unitless 0-1) and `--wom-trees-lift` (px); the CSS does the rest.
 */
class WomTrees extends HTMLElement {
  /** @type {HTMLElement | null} */
  #wrapper = null;

  /** @type {HTMLElement | null} */
  #footer = null;

  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #frame = null;

  #progress = 0;

  #pinned = false;

  #visible = false;

  connectedCallback() {
    this.#setProgress(0);

    this.#wrapper = this.closest('.wom-trees-section');
    if (!this.#wrapper) return;

    // Absent footer just means no end-of-page lift - never guess at the page bottom.
    this.#footer = document.querySelector('footer');
    this.#scroller = document.querySelector('.page-wrapper');

    this.#observer = new IntersectionObserver(
      (entries) => {
        this.#visible = entries.some((entry) => entry.isIntersecting);
        this.#visible ? this.#start() : this.#stopIfIdle();
      },
      { rootMargin: ROOT_MARGIN }
    );

    this.#observer.observe(this.#wrapper);
    this.#settle();
  }

  // Loading already scrolled past the section leaves the observer reporting no intersection, so nothing
  // would ever start the loop and the trees would stay unpinned until the first scroll back up.
  #settle() {
    if (!this.#wrapper) return;

    const rect = this.#wrapper.getBoundingClientRect();

    this.#updatePin(rect);
    this.#updateLift();
    this.#updateScrollbarWidth();

    // Snap rather than ease - the entrance animation should not replay on load.
    if (!prefersReducedMotion()) {
      this.#progress = this.#targetProgress(rect);
      this.#setProgress(this.#progress);
    }

    if (this.#pinned) this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#wrapper = null;
    this.#footer = null;
    this.#scroller = null;
  }

  #start() {
    if (this.#frame != null) return;
    this.#frame = requestAnimationFrame(this.#tick);
  }

  #stop() {
    if (this.#frame == null) return;
    cancelAnimationFrame(this.#frame);
    this.#frame = null;
  }

  // While pinned the artwork is still on screen long after the wrapper has scrolled away, and the
  // end-of-page lift has to keep tracking - so only idle out once both are false.
  #stopIfIdle() {
    if (this.#pinned) return;
    this.#stop();
  }

  #tick = () => {
    if (this.#wrapper) {
      const rect = this.#wrapper.getBoundingClientRect();

      this.#updatePin(rect);
      this.#updateLift();
      this.#updateScrollbarWidth();

      // Reduced motion keeps the pin and the lift but drops the scroll-linked slide.
      if (!prefersReducedMotion()) this.#updateProgress(rect);

      if (!this.#visible && !this.#pinned) return this.#stop();
    }

    this.#frame = requestAnimationFrame(this.#tick);
  };

  /** @param {DOMRect} rect */
  #updatePin(rect) {
    const shouldPin = rect.bottom <= window.innerHeight;
    if (shouldPin === this.#pinned) return;

    this.#pinned = shouldPin;
    this.toggleAttribute('data-pinned', shouldPin);
  }

  // Mirrors native sticky's push at the end of its containing block: flush to the viewport bottom
  // until the last stretch of page, then rising 1:1 with scroll to sit that far up at the very bottom.
  #updateLift() {
    if (!this.#footer) return;

    // vw, so it derives from innerWidth - the animation length below is vh and uses innerHeight.
    const liftVw = MOBILE_QUERY.matches ? END_LIFT_VW_MOBILE : END_LIFT_VW;
    const maxLift = (liftVw / 100) * window.innerWidth;
    const remaining = this.#footer.getBoundingClientRect().bottom - window.innerHeight;
    const lift = Math.min(Math.max(maxLift - remaining, 0), maxLift);

    this.style.setProperty('--wom-trees-lift', `${lift}px`);
  }

  // Fixed positioning resolves against the viewport, which includes .page-wrapper's scrollbar, so the
  // pinned layer would otherwise paint over it. offsetWidth - clientWidth is the gutter, and is
  // naturally 0 below 990px where .page-wrapper is not a scroll container.
  #updateScrollbarWidth() {
    const gutter = this.#scroller ? this.#scroller.offsetWidth - this.#scroller.clientWidth : 0;
    this.style.setProperty('--wom-scrollbar-width', `${gutter}px`);
  }

  // Mirrors Webflow's SCROLLING_IN_VIEW with startsExiting - 0 when the section's bottom edge sits at the viewport bottom, 1 once it has travelled the animation length.
  /** @param {DOMRect} rect */
  #targetProgress(rect) {
    const viewportHeight = window.innerHeight || 1;
    const lengthVh = Number(this.dataset.animationLength);
    const length = Math.max(
      ((Number.isFinite(lengthVh) ? lengthVh : DEFAULT_ANIMATION_LENGTH_VH) / 100) * viewportHeight,
      1
    );

    return Math.min(Math.max((viewportHeight - rect.bottom) / length, 0), 1);
  }

  /** @param {DOMRect} rect */
  #updateProgress(rect) {
    const target = this.#targetProgress(rect);
    const delta = target - this.#progress;

    this.#progress = Math.abs(delta) < EPSILON ? target : this.#progress + delta * SMOOTHING;
    this.#setProgress(this.#progress);
  }

  /** @param {number} value */
  #setProgress(value) {
    this.style.setProperty('--wom-trees-progress', String(value));
  }
}

if (!customElements.get('wom-trees')) {
  customElements.define('wom-trees', WomTrees);
}
